import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { Button, Card, Spinner, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiUser, FiSave, FiTrash2 } from 'react-icons/fi';
import { FaBuilding } from 'react-icons/fa';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import ZForm from './../components/ZForm';

export default function Customer() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [customer, setCustomer] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isCreating = id === 'create';

    // Load customer
    const loadCustomer = () => {
        if (isCreating) {
            setCustomer({});
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        tre.api('GET', '/customers/' + id, {
            context: context,
            success: (r) => {
                setCustomer(r);
                setIsLoading(false);
            },
            error: () => {
                setIsLoading(false);
                navigate('/customers');
            }
        });
    };

    useEffect(() => {
        loadCustomer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleForm = ({ name, value }) => {
        setCustomer({ ...customer, [name]: value });
    };

    const save = () => {
        setIsSaving(true);
        if (isCreating) {
            tre.api('POST', '/companies/' + context.idCompany + '/customers', {
                context: context,
                params: customer,
                success: (r) => {
                    NotificationManager.success('Cliente creado');
                    setIsSaving(false);
                    navigate('/customer/' + r.id);
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', '/customers/' + id, {
                context: context,
                params: customer,
                success: () => {
                    NotificationManager.success('Cliente modificado');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    const remove = () => {
        setIsDeleting(true);
        tre.api('DELETE', '/customers/' + id, {
            context: context,
            success: () => {
                NotificationManager.success('Cliente eliminado');
                setShowDeleteModal(false);
                navigate('/customers');
            },
            error: () => setIsDeleting(false)
        });
    };

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '700px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando cliente...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '700px' }}>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                    <Button
                        variant="link"
                        onClick={() => navigate('/customers')}
                        className="p-0 me-3"
                        style={{ color: '#5f6368' }}
                    >
                        <FiArrowLeft size={24} />
                    </Button>
                    <div>
                        <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                            <FiUser className="me-2" style={{ color: '#1a73e8' }} />
                            {isCreating ? 'Nuevo Cliente' : (customer.name || 'Cliente')}
                        </h4>
                        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                            {isCreating ? 'Crear nuevo cliente' : 'Editar información del cliente'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Customer Info Card */}
            <Card className="mb-4" style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{ width: '40px', height: '40px', backgroundColor: '#e8f0fe', color: '#1a73e8' }}
                        >
                            <FaBuilding size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Datos del Cliente</h6>
                            <small className="text-muted">Información de contacto y facturación</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <ZForm
                        elements={[
                            { label: 'Nombre', md: 8, name: 'name', value: customer.name || '' },
                            { label: 'NIF', md: 4, name: 'nif', value: customer.nif || '' },
                            { label: 'Dirección', md: 12, name: 'address', value: customer.address || '' },
                            { label: 'Ciudad', md: 8, name: 'city', value: customer.city || '' },
                            { label: 'Teléfono', md: 4, name: 'phone', value: customer.phone || '' },
                        ]}
                        onChange={handleForm}
                    />
                </Card.Body>
            </Card>

            {/* Actions */}
            <div className="d-flex justify-content-between">
                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={isSaving}
                    >
                        {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                        {isSaving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button
                        variant="light"
                        onClick={() => navigate('/customers')}
                    >
                        Cancelar
                    </Button>
                </div>

                {!isCreating && (
                    <Button
                        variant="outline-danger"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isDeleting}
                    >
                        <FiTrash2 className="me-2" />
                        Eliminar
                    </Button>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#202124', fontWeight: 500 }}>Confirmar eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">¿Estás seguro de que quieres eliminar a <strong>{customer.name}</strong>?</p>
                    <p className="text-muted small mb-0">Esta acción no se puede deshacer.</p>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: 'none' }}>
                    <Button variant="light" onClick={() => setShowDeleteModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={remove} disabled={isDeleting}>
                        {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                        {isDeleting ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
